import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

interface DialogContentProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** Hide the visible header (title stays for screen readers). */
  hideHeader?: boolean;
  hideClose?: boolean;
  closeLabel?: string;
}

const overlayCls = 'fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in';

export function DialogContent({
  title,
  description,
  children,
  footer,
  className,
  hideHeader,
  hideClose,
  closeLabel = 'Close',
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlayCls} />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 rounded-xl border border-border bg-popover text-popover-foreground shadow-pop focus:outline-none data-[state=open]:animate-zoom-in',
          className,
        )}
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <div className={cn('px-5 pt-4', hideHeader && 'sr-only')}>
          <DialogPrimitive.Title className="text-[15px] font-semibold">{title}</DialogPrimitive.Title>
          {description && (
            <DialogPrimitive.Description className="mt-1 text-[13px] text-muted-foreground">{description}</DialogPrimitive.Description>
          )}
        </div>
        {children && <div className={cn('px-5', hideHeader ? 'py-4' : 'py-4')}>{children}</div>}
        {footer && <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">{footer}</div>}
        {!hideClose && (
          <DialogPrimitive.Close
            className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label={closeLabel}
          >
            <X className="size-4" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/** Side drawer (mobile navigation, detail panels). */
export function SheetContent({
  title,
  children,
  side = 'left',
  className,
}: {
  title: string;
  children: ReactNode;
  side?: 'left' | 'right';
  className?: string;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlayCls} />
      <DialogPrimitive.Content
        aria-describedby={undefined}
        className={cn(
          'fixed inset-y-0 z-50 flex w-72 max-w-[85vw] flex-col bg-sidebar shadow-pop focus:outline-none',
          side === 'left' ? 'left-0 border-r border-sidebar-border data-[state=open]:animate-slide-in-left' : 'right-0 border-l',
          className,
        )}
      >
        <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
